import DOMPurify from 'dompurify';

import type { BadgeVariant } from '@/components/core/Badge';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';

/** Visible empty value from `task-view-in-editor.tpl.html` (`|| 'none'`). */
const EMPTY_TASK_VALUE = 'none';

type TaskTransition = {
    key: string;
    text?: string;
    isDefaultTransition?: boolean;
};

type EditorTask = {
    id: number | string;
    title?: string;
    dueDate?: string | number | Date | null;
    createDate?: string | number | Date | null;
    author?: string;
    assignee?: string;
    notes?: string;
    tags?: string[];
    taskListId?: number | string;
    taskStatus?: string;
    transitions?: TaskTransition[];
    chosenTransition?: string;
    items?: string[];
    selectionId?: string;
    uploadGathering?: string;
    itemTag?: string;
};

type TaskAttachment = {
    url: string;
    name: string;
    contentType?: string;
};

type TaskComment = {
    id?: number;
    author?: string;
    createDate?: string | number | Date | null;
    text?: string;
    assets?: TaskAttachment[];
};

const FILE_TYPE_GROUPS: Record<string, string[]> = {
    image: ['jpg', 'jpeg', 'gif', 'png', 'bmp', 'svg', 'image'],
    code: ['css', 'html', 'rb', 'js', 'json', 'xml', 'code'],
    text: ['txt', 'csv', 'rtf', 'odt', 'text'],
    zip: ['zip', 'gz', 'archive'],
    excel: ['xls', 'xlsx', 'excel'],
    word: ['doc', 'docx', 'word'],
    powerpoint: ['ppt', 'pptx', 'powerpoint'],
    pdf: ['pdf'],
};

const isEditorTask = (value: unknown): value is EditorTask => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const id = (value as { id?: unknown }).id;
    return typeof id === 'number' || (typeof id === 'string' && id !== '');
};

const taskStatusBadgeVariant = (status?: string): BadgeVariant => {
    switch (status) {
        case 'OPEN':
            return 'warning';
        case 'FINISHED':
            return 'success';
        default:
            return 'neutral';
    }
};

const commentsOldestFirst = (comments: TaskComment[]): TaskComment[] => {
    return [...comments].sort((left, right) => {
        const leftTime = left.createDate ? new Date(left.createDate).getTime() : 0;
        const rightTime = right.createDate ? new Date(right.createDate).getTime() : 0;
        const safeLeft = Number.isNaN(leftTime) ? 0 : leftTime;
        const safeRight = Number.isNaN(rightTime) ? 0 : rightTime;
        return safeLeft - safeRight;
    });
};

const isImageAttachment = (contentType?: string): boolean => {
    return !!contentType && contentType.toLowerCase().includes('image');
};

const downloadUrl = (url: string): string => `${url}?download=true`;

const fileTypeIconName = (fileName?: string): string => {
    if (!fileName) {
        return 'file';
    }

    const dot = fileName.lastIndexOf('.');
    const extension = (dot >= 0 ? fileName.slice(dot + 1) : fileName).toLowerCase();
    const match = Object.entries(FILE_TYPE_GROUPS).find(([, extensions]) => extensions.includes(extension));
    return match ? match[0] : extension || 'file';
};

const sanitizeTaskHtml = (html?: string): string => {
    if (!html) {
        return '';
    }

    return DOMPurify.sanitize(html);
};

const userDisplayName = (userId?: string): string => {
    if (!userId) {
        return '';
    }

    try {
        const rootScope = getRootScope(getInjector());
        const name = rootScope?.getUserName?.(userId);
        return name || userId;
    } catch {
        return userId;
    }
};

/**
 * Title, due date, notes, and tags use this check. An empty role list can edit,
 * matching `EditTaskController#hasEditPermission`.
 */
const canEditTask = (task: EditorTask): boolean => {
    try {
        const user = getRootScope(getInjector()).user;
        const roles = user?.roles;
        if (task && user && Array.isArray(roles) && roles.length > 0) {
            return roles.includes('admin') || String(user.userId) === String(task.author);
        }
    } catch {
        return true;
    }
    return true;
};

const isTaskFinished = (task: EditorTask): boolean => task.taskStatus === 'FINISHED';

/** Defined task tags from the data model. `undefined` hides the tags row. */
const definedTaskTags = (): string[] | undefined => {
    try {
        const tags = getRootScope(getInjector()).dataModel?.getTaskTags?.();
        if (tags == null) {
            return undefined;
        }
        return Array.isArray(tags) ? tags.map(String) : undefined;
    } catch {
        return undefined;
    }
};

const canReopenTask = (task: EditorTask): boolean => {
    if (task.id == null || task.id === '') {
        return false;
    }

    try {
        const rootScope = getRootScope(getInjector());
        const customService = rootScope?.getService?.('CustomTaskEditorSettings');
        if (!customService) {
            return true;
        }
        return !!customService.enableReopenTask?.(task, rootScope.user, rootScope.organization);
    } catch {
        return true;
    }
};

const currentTaskUser = (): { userId?: string; imageUrl?: string } => {
    try {
        const user = getRootScope(getInjector()).user;
        return {
            userId: user?.userId,
            imageUrl: user?.imageUrl,
        };
    } catch {
        return {};
    }
};

/** More than one related item, an import gathering, or a stored selection. A single item opens in the editor instead. */
const showsRelatedItemBrowse = (task: EditorTask): boolean => {
    const singleItem = Array.isArray(task.items) && task.items.length === 1;
    const hasGroup = !!(task.items?.length || task.uploadGathering || task.selectionId);
    return (!singleItem && hasGroup) || !!task.selectionId;
};

const transitionLabel = (task: EditorTask): string => {
    if (!task.chosenTransition) {
        return '';
    }
    const match = task.transitions?.find((transition) => transition.key === task.chosenTransition);
    return match?.text || task.chosenTransition;
};

const userImageUrl = (userId?: string): string | undefined => {
    if (!userId) {
        return undefined;
    }

    try {
        const rootScope = getRootScope(getInjector());
        if (!rootScope?.userHasImage?.(userId)) {
            return undefined;
        }

        const imageUrl = rootScope.getUserImageUrl?.(userId);
        return imageUrl || undefined;
    } catch {
        return undefined;
    }
};

export type { EditorTask, TaskAttachment, TaskComment, TaskTransition };
export {
    canEditTask,
    canReopenTask,
    commentsOldestFirst,
    currentTaskUser,
    definedTaskTags,
    downloadUrl,
    EMPTY_TASK_VALUE,
    fileTypeIconName,
    isEditorTask,
    isImageAttachment,
    isTaskFinished,
    sanitizeTaskHtml,
    showsRelatedItemBrowse,
    taskStatusBadgeVariant,
    transitionLabel,
    userDisplayName,
    userImageUrl,
};
